import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import { WidgetProps } from '@rjsf/utils';
import { getDefaultRegistry } from '@rjsf/core';
import { registerJqLanguage } from 'components/routes/jqLanguage';
import { MONACO_OPTIONS } from 'components/routes/schema-builder/editorConfig';

const CODE_LANGUAGES = ['jq'];
const LINE_HEIGHT = 20;
const VERTICAL_PADDING = 16;
const MAX_LINES = 20;

// Replaces rjsf's TextareaWidget at every nesting level. A connector opts a
// textarea into code editing with `ui:options.language`, which portals without
// this widget ignore and render as a plain textarea.
const CodeTextareaWidget = (props: WidgetProps) => {
  const language = props.options?.language;
  if (typeof language !== 'string' || !CODE_LANGUAGES.includes(language)) {
    const DefaultTextareaWidget = getDefaultRegistry().widgets.TextareaWidget;
    return <DefaultTextareaWidget {...props} />;
  }
  return <CodeEditor {...props} language={language} />;
};

const CodeEditor = (props: WidgetProps & { language: string }) => {
  const {
    id,
    value,
    disabled,
    readonly,
    onChange,
    onBlur,
    onFocus,
    options,
    language,
  } = props;
  const minLines = Math.max(Number(options?.rows) || 4, 1);
  const [height, setHeight] = useState(
    minLines * LINE_HEIGHT + VERTICAL_PADDING,
  );
  const readOnly = !!(disabled || readonly);
  const emit = (text?: string) => onChange(!text ? options?.emptyValue : text);
  const DefaultTextareaWidget = getDefaultRegistry().widgets.TextareaWidget;

  return (
    <div
      id={id}
      data-testid={`CodeTextareaWidget-${language}-div`}
      className="w-full overflow-hidden rounded border border-unlisted-neutral-gray-400"
      style={{ height }}
      onFocus={() => onFocus(id, value)}
      onBlur={() => onBlur(id, value)}
    >
      <Editor
        height="100%"
        language={language}
        value={value ?? ''}
        theme="vs"
        options={{ ...MONACO_OPTIONS(readOnly), wordWrap: 'on' }}
        beforeMount={registerJqLanguage}
        onMount={(editor) => {
          const fit = () =>
            setHeight(
              Math.min(
                Math.max(
                  editor.getContentHeight(),
                  minLines * LINE_HEIGHT + VERTICAL_PADDING,
                ),
                MAX_LINES * LINE_HEIGHT + VERTICAL_PADDING,
              ),
            );
          fit();
          editor.onDidContentSizeChange(fit);
        }}
        onChange={emit}
        // Monaco loads from a CDN; until it arrives (or if it never does) the field stays editable.
        loading={<DefaultTextareaWidget {...props} />}
      />
    </div>
  );
};

export default CodeTextareaWidget;
